import {
  RunRateType,
  SELLING_UOM_DECIMAL_SHIFT,
  SELLING_UOM_MULTIPLIER,
  type SellingUnitOfMeasure,
} from '@snap-types/models/enums';
import { Decimal } from '@snap-types/types/decimal';

/**
 * Conversion between SNAP's canonical storage basis and a site's selling unit
 * of measure.
 *
 * SNAP stores every quantity, and every quantity-derived work order value, in
 * eaches. A site sells in its own unit, so values have to be converted whenever
 * they cross into that site's world — most consequentially when writing to
 * Visual, where quote-level rows belong to the quote site while work order,
 * operation and requirement rows belong to the manufacturer site. Callers pick
 * the target; this module does the arithmetic.
 */

declare const UNIT_BASIS: unique symbol;

/** A value in SNAP's canonical basis. */
export type Eaches<T> = T & { readonly [UNIT_BASIS]: 'eaches' };

/** A value converted into a specific target site's selling unit of measure. */
export type SiteUnits<T> = T & { readonly [UNIT_BASIS]: 'siteUnits' };

/**
 * Asserts that a stored value is in canonical eaches.
 *
 * This is the one place the assumption is stated, so converting a value twice
 * or converting an already-converted one stops type-checking.
 */
export function eaches(value: Decimal): Eaches<Decimal> {
  return value as Eaches<Decimal>;
}

/**
 * Asserts that a value is expressed in a site's selling unit of measure —
 * typically something a user just typed, or a value read back out of Visual.
 */
export function siteUnits(value: Decimal): SiteUnits<Decimal> {
  return value as SiteUnits<Decimal>;
}

export type SellingUomTarget = {
  sellingUnitOfMeasure: SellingUnitOfMeasure;

  /**
   * Purely for legibility at call sites that have it — nothing here reads it.
   * Optional because the quote builder converts for the site it is already
   * pinned to and has no reason to carry a code around.
   */
  siteCode?: string;
};

function multiplierFor(target: SellingUomTarget) {
  return SELLING_UOM_MULTIPLIER[target.sellingUnitOfMeasure];
}

/**
 * Precision follows the unit, the same way value does.
 *
 * A site's configured `decimalPlaces` describes its price *as quoted* — ADF
 * quotes to the cent per thousand. SNAP stores per-unit values in eaches, where
 * that same grid is five decimal places, so rounding a stored per-unit value to
 * the bare `decimalPlaces` would coarsen the site's price grid by 1000× rather
 * than round it.
 *
 * Only per-unit values shift. A currency total is the same number of dollars in
 * either basis and keeps `decimalPlaces` untouched.
 */
export function perUnitDecimalPlaces(
  decimalPlaces: number,
  sellingUnitOfMeasure: SellingUnitOfMeasure,
): number {
  return decimalPlaces + SELLING_UOM_DECIMAL_SHIFT[sellingUnitOfMeasure];
}

function convertIfDefined(
  value: Decimal | null | undefined,
  convert: (value: Eaches<Decimal>) => SiteUnits<Decimal>,
) {
  return value == null ? value : convert(eaches(value));
}

function convertIfDefinedFromSiteUnits(
  value: Decimal | null | undefined,
  convert: (value: SiteUnits<Decimal>) => Eaches<Decimal>,
) {
  return value == null ? value : convert(siteUnits(value));
}

/**
 * A count of pieces. Larger in eaches than in the target's units, so it
 * divides: 25,000 eaches is 25 at a site selling in thousands.
 */
export function quantityToSiteUnits(
  value: Eaches<Decimal>,
  target: SellingUomTarget,
): SiteUnits<Decimal> {
  return value.dividedBy(multiplierFor(target)) as SiteUnits<Decimal>;
}

/**
 * A value expressed *per* piece — a price, a cost, a material ratio. Inverted
 * relative to quantity, so it multiplies: $0.01 each is $10 per thousand.
 */
export function perQuantityToSiteUnits(
  value: Eaches<Decimal>,
  target: SellingUomTarget,
): SiteUnits<Decimal> {
  return value.times(multiplierFor(target)) as SiteUnits<Decimal>;
}

/**
 * The inverse of {@link quantityToSiteUnits}, for values arriving from a site's
 * world rather than leaving for one — a quantity a user typed, or a quantity
 * read out of Visual. 25 at a site selling in thousands is 25,000 eaches.
 */
export function quantityFromSiteUnits(
  value: SiteUnits<Decimal>,
  target: SellingUomTarget,
): Eaches<Decimal> {
  return value.times(multiplierFor(target)) as Eaches<Decimal>;
}

/**
 * The inverse of {@link perQuantityToSiteUnits}. $10 per thousand is $0.01
 * each.
 */
export function perQuantityFromSiteUnits(
  value: SiteUnits<Decimal>,
  target: SellingUomTarget,
): Eaches<Decimal> {
  return value.dividedBy(multiplierFor(target)) as Eaches<Decimal>;
}

/**
 * Which way a run rate converts depends on where the piece count sits in the
 * unit. Load-based rates carry no piece count at all and are basis-independent.
 */
export function runToSiteUnits(
  value: Eaches<Decimal>,
  runType: RunRateType,
  target: SellingUomTarget,
): SiteUnits<Decimal> {
  switch (runType) {
    case RunRateType.PCS_PER_HR:
    case RunRateType.PCS_PER_MIN:
    case RunRateType.PCS_PER_DAY:
      return quantityToSiteUnits(value, target);

    case RunRateType.HRS_PER_PC:
    case RunRateType.MIN_PER_PC:
    case RunRateType.DAYS_PER_PC:
      return perQuantityToSiteUnits(value, target);

    case RunRateType.HRS_PER_LOAD:
    case RunRateType.DAYS_PER_LOAD:
    case RunRateType.LOADS_PER_HR:
    case RunRateType.LOADS_PER_DAY:
      return value as unknown as SiteUnits<Decimal>;
  }
}

/** The inverse of {@link runToSiteUnits}. */
export function runFromSiteUnits(
  value: SiteUnits<Decimal>,
  runType: RunRateType,
  target: SellingUomTarget,
): Eaches<Decimal> {
  switch (runType) {
    case RunRateType.PCS_PER_HR:
    case RunRateType.PCS_PER_MIN:
    case RunRateType.PCS_PER_DAY:
      return quantityFromSiteUnits(value, target);

    case RunRateType.HRS_PER_PC:
    case RunRateType.MIN_PER_PC:
    case RunRateType.DAYS_PER_PC:
      return perQuantityFromSiteUnits(value, target);

    case RunRateType.HRS_PER_LOAD:
    case RunRateType.DAYS_PER_LOAD:
    case RunRateType.LOADS_PER_HR:
    case RunRateType.LOADS_PER_DAY:
      return value as unknown as Eaches<Decimal>;
  }
}

export type PricedQuantityInEaches = {
  quantity: Decimal;
  unitPrice: Decimal;
  unitCost?: Decimal | null;
  totalPrice: Decimal;
};

/**
 * Quantity and the prices that hang off it convert together or not at all.
 *
 * Dividing the quantity while multiplying the price leaves their product
 * untouched, which is why `totalPrice` passes through unchanged rather than
 * being recomputed. Converting either side alone silently rescales the line
 * total.
 */
export function pricedQuantityToSiteUnits(
  price: PricedQuantityInEaches,
  target: SellingUomTarget,
) {
  return {
    quantity: quantityToSiteUnits(eaches(price.quantity), target),
    unitPrice: perQuantityToSiteUnits(eaches(price.unitPrice), target),
    unitCost: convertIfDefined(price.unitCost, (value) =>
      perQuantityToSiteUnits(value, target),
    ),
    totalPrice: price.totalPrice,
  };
}

export type CalculatedQuantitiesInEaches = {
  startQuantity: Decimal;
  endQuantity: Decimal;
};

/**
 * Operation quantities produced by `calculateWorkOrderCosts`, which runs
 * entirely in eaches.
 */
export function calculatedQuantitiesToSiteUnits(
  quantities: CalculatedQuantitiesInEaches,
  target: SellingUomTarget,
) {
  return {
    startQuantity: quantityToSiteUnits(
      eaches(quantities.startQuantity),
      target,
    ),
    endQuantity: quantityToSiteUnits(eaches(quantities.endQuantity), target),
  };
}

export type OperationFieldsInEaches = {
  runType: RunRateType;
  run?: Decimal | null;
  loadSizeQty?: Decimal | null;
  fixedScrapUnits?: Decimal | null;
  runCostPerUnit?: Decimal | null;
  serviceChargePerUnit?: Decimal | null;
  burPerUnitRun?: Decimal | null;
};

/**
 * The full set of operation fields that carry a piece count, converted as a
 * unit so no individual field can be missed or converted the wrong way.
 *
 * Everything else on a `SnapOperation` — setup hours, per-hour rates, percentage
 * scrap — is independent of the selling unit of measure and is deliberately
 * absent.
 */
export function operationFieldsToSiteUnits(
  operation: OperationFieldsInEaches,
  target: SellingUomTarget,
) {
  return {
    run: convertIfDefined(operation.run, (value) =>
      runToSiteUnits(value, operation.runType, target),
    ),
    loadSizeQty: convertIfDefined(operation.loadSizeQty, (value) =>
      quantityToSiteUnits(value, target),
    ),
    fixedScrapUnits: convertIfDefined(operation.fixedScrapUnits, (value) =>
      quantityToSiteUnits(value, target),
    ),
    runCostPerUnit: convertIfDefined(operation.runCostPerUnit, (value) =>
      perQuantityToSiteUnits(value, target),
    ),
    serviceChargePerUnit: convertIfDefined(
      operation.serviceChargePerUnit,
      (value) => perQuantityToSiteUnits(value, target),
    ),
    burPerUnitRun: convertIfDefined(operation.burPerUnitRun, (value) =>
      perQuantityToSiteUnits(value, target),
    ),
  };
}

/**
 * The inverse of {@link operationFieldsToSiteUnits}, for operations authored
 * against a site's own numbers — the configurator, which is a Visual client and
 * works in site units throughout — on their way into canonical storage.
 */
export function operationFieldsFromSiteUnits(
  operation: OperationFieldsInEaches,
  target: SellingUomTarget,
) {
  return {
    run: convertIfDefinedFromSiteUnits(operation.run, (value) =>
      runFromSiteUnits(value, operation.runType, target),
    ),
    loadSizeQty: convertIfDefinedFromSiteUnits(operation.loadSizeQty, (value) =>
      quantityFromSiteUnits(value, target),
    ),
    fixedScrapUnits: convertIfDefinedFromSiteUnits(
      operation.fixedScrapUnits,
      (value) => quantityFromSiteUnits(value, target),
    ),
    runCostPerUnit: convertIfDefinedFromSiteUnits(
      operation.runCostPerUnit,
      (value) => perQuantityFromSiteUnits(value, target),
    ),
    serviceChargePerUnit: convertIfDefinedFromSiteUnits(
      operation.serviceChargePerUnit,
      (value) => perQuantityFromSiteUnits(value, target),
    ),
    burPerUnitRun: convertIfDefinedFromSiteUnits(
      operation.burPerUnitRun,
      (value) => perQuantityFromSiteUnits(value, target),
    ),
  };
}

export type OrderLineFieldsInSiteUnits = {
  orderQuantity: Decimal;
  invoicedQuantity?: Decimal | null;
  unitPrice: Decimal;
};

/**
 * Order lines mirror Visual, so their quantities and prices are in the selling
 * unit of the site that took the order. This converts them on the way into
 * SNAP's domain.
 *
 * Only these three fields need it. An order line's totals — invoiced amount,
 * total labor/material/service/burden cost — are absolute and identical in
 * either basis, and every per-unit cost and margin SNAP derives from an order
 * line is computed as a total divided by the quantity. Converting the quantity
 * therefore converts the whole derived set exactly once, which is why
 * {@link orderLineDetails} must run on the converted line rather than the raw
 * one.
 *
 * That the derived margins are ratios makes them invariant under this
 * transform, so a margin that moves is a reliable sign the pair came apart.
 */
export function orderLineFieldsFromSiteUnits(
  orderLine: OrderLineFieldsInSiteUnits,
  target: SellingUomTarget,
) {
  return {
    orderQuantity: quantityFromSiteUnits(
      siteUnits(orderLine.orderQuantity),
      target,
    ),
    invoicedQuantity: convertIfDefinedFromSiteUnits(
      orderLine.invoicedQuantity,
      (value) => quantityFromSiteUnits(value, target),
    ),
    unitPrice: perQuantityFromSiteUnits(siteUnits(orderLine.unitPrice), target),
  };
}

export type RequirementFieldsInEaches = {
  qtyPer?: Decimal | null;
};

/**
 * The inverse of {@link requirementFieldsToSiteUnits}.
 *
 * As with the outbound direction, a requirement's unit costs, `fixedQty` and
 * `fixedCost` are deliberately absent — they are per requirement unit or
 * absolute, and do not scale with the selling unit of measure.
 *
 * `calcQty` is absent for a subtler reason. It is derived as
 * `fixedQty + qtyPer × operationQuantity`, and those two factors convert in
 * opposite directions, so the product is invariant — the same reason
 * `totalPrice` passes through {@link pricedQuantityToSiteUnits} untouched.
 * Converting it would double-scale.
 */
export function requirementFieldsFromSiteUnits(
  requirement: RequirementFieldsInEaches,
  target: SellingUomTarget,
) {
  return {
    qtyPer: convertIfDefinedFromSiteUnits(requirement.qtyPer, (value) =>
      perQuantityFromSiteUnits(value, target),
    ),
  };
}

/**
 * `qtyPer` is the only requirement field tied to the finished-goods quantity.
 *
 * A requirement's `unitMaterialCost`, `unitLaborCost`, `unitBurdenCost` and
 * `unitServiceCost` are costs per *requirement* unit — per pound of wire, not
 * per finished piece — and `fixedQty` and `fixedCost` are absolute. None of
 * them scale with the selling unit of measure, so converting them would be
 * wrong. Note that `SnapQuotePrice` carries those same four cost names meaning
 * something different: there they are per finished piece and they do convert.
 */
export function requirementFieldsToSiteUnits(
  requirement: RequirementFieldsInEaches,
  target: SellingUomTarget,
) {
  return {
    qtyPer: convertIfDefined(requirement.qtyPer, (value) =>
      perQuantityToSiteUnits(value, target),
    ),
  };
}
